import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Modal } from "../lawyer/LawyerUi";
import { apiErrorMessage } from "../../services/userApi";

const STRIPE_JS = "https://js.stripe.com/v3/";

export const loadStripeJs = () =>
  new Promise((resolve, reject) => {
    if (window.Stripe) return resolve(window.Stripe);
    let script = document.querySelector(`script[src="${STRIPE_JS}"]`);
    if (!script) {
      script = document.createElement("script");
      script.src = STRIPE_JS;
      script.async = true;
      document.body.appendChild(script);
    }
    script.addEventListener("load", () => resolve(window.Stripe));
    script.addEventListener("error", () => reject(new Error("Failed to load Stripe.js")));
    return undefined;
  });

const dark = () => document.documentElement.hasAttribute("data-lwp-dark");

/**
 * Card payment sheet shared by every paid action in the portal.
 *
 * prepare()  → API call creating the PaymentIntent; must resolve the axios
 *              response whose data.data carries { paymentIntent, publishableKey }.
 * onPaid(pi) → runs after Stripe confirms; the sheet closes afterwards either
 *              way (errors are toasted), so a charged card is never re-submitted.
 * Intents with manual capture settle as "requires_capture" — both that and
 * "succeeded" count as paid.
 */
const StripeCardModal = ({ open, onClose, title = "Payment", amountLabel, submitLabel = "Pay", billing, prepare, onPaid, note }) => {
  const [status, setStatus] = useState("idle"); // idle | preparing | ready | paying
  const stripeRef = useRef(null);
  const cardRef = useRef(null);
  const secretRef = useRef(null);
  const mountRef = useRef(null);
  const prepareRef = useRef(prepare);
  prepareRef.current = prepare;

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    let card = null;
    setStatus("preparing");

    (async () => {
      try {
        const res = await prepareRef.current();
        const data = res?.data?.data;
        if (!res?.data?.status || !data?.paymentIntent || !data?.publishableKey) {
          throw new Error(res?.data?.message || "Unable to start the payment.");
        }
        const Stripe = await loadStripeJs();
        if (cancelled) return;
        secretRef.current = data.paymentIntent;
        stripeRef.current = Stripe(data.publishableKey);
        card = stripeRef.current.elements().create("card", {
          hidePostalCode: true,
          style: { base: { fontSize: "16px", color: dark() ? "#e6e6e8" : "#222222", "::placeholder": { color: "#9a9aa2" } } },
        });
        card.mount(mountRef.current);
        cardRef.current = card;
        setStatus("ready");
      } catch (err) {
        if (cancelled) return;
        toast.error(apiErrorMessage(err, err?.message || "Unable to start the payment."));
        onClose();
      }
    })();

    return () => {
      cancelled = true;
      if (card) {
        try {
          card.destroy();
        } catch {
          // already gone
        }
      }
      cardRef.current = null;
      stripeRef.current = null;
      secretRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pay = async () => {
    if (status !== "ready" || !stripeRef.current || !cardRef.current) return;
    setStatus("paying");
    let paymentIntent;
    try {
      const result = await stripeRef.current.confirmCardPayment(secretRef.current, {
        payment_method: { card: cardRef.current, billing_details: billing || {} },
      });
      if (result.error) throw new Error(result.error.message || "Payment failed.");
      const st = result.paymentIntent?.status;
      if (st !== "succeeded" && st !== "requires_capture") throw new Error("Payment could not be completed.");
      paymentIntent = result.paymentIntent;
    } catch (err) {
      // Nothing was charged — let them fix the card and try again.
      toast.error(apiErrorMessage(err, err?.message || "Payment failed."));
      setStatus("ready");
      return;
    }
    // The card has been charged (or held) from here on: never leave the sheet
    // open for a second attempt, even if the follow-up API call fails.
    try {
      await onPaid(paymentIntent);
    } catch (err) {
      toast.error(
        apiErrorMessage(err, err?.message || `Payment went through but something went wrong. Please contact support (ref ${paymentIntent.id}).`)
      );
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={() => status !== "paying" && onClose()}
      title={title}
      className="usp-pay-modal"
      footer={
        <button type="button" className="lwp-btn" onClick={pay} disabled={status !== "ready"}>
          {status === "paying" || status === "preparing" ? (
            <span className="spinner-border spinner-border-sm" />
          ) : (
            <>
              <i className="bi bi-lock-fill me-2" />
              {submitLabel}
            </>
          )}
        </button>
      }
    >
      {amountLabel && (
        <div className="usp-pay-amount">
          <span>Total</span>
          <strong>{amountLabel}</strong>
        </div>
      )}
      <div className="usp-card-box">
        {/* Stripe owns this node's children; keep React content out of it. */}
        <div ref={mountRef} />
        {status === "preparing" && <span className="lwp-muted">Preparing secure payment…</span>}
      </div>
      <p className="usp-pay-note">
        <i className="bi bi-shield-lock-fill" /> {note || "Payments are processed securely by Stripe."}
      </p>
    </Modal>
  );
};

export default StripeCardModal;
